import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';

import { Column } from '../../../shared/interfaces/rms-interfaces';
import { CheckboxFieldComponent } from '../checkbox-field/checkbox-field.component';
import { RmsDateComponent } from '../date-field/date-field.component';
import { SelectFieldComponent } from '../select-field/select-field.component';
import { InputFieldComponent } from '../input-field/input-field.component';
import { NumberFieldComponent } from '../number-field/number-field.component';
import {
  FilterMap,
  FilterPreset,
  FilterService
} from '../../../services/filter-panel.service';
import { RmsMessageService } from '../../../services/rms-message.service';

export type Filter = {
  FilterType: string;
  value: any;
};

@Component({
  selector: 'app-filter-panel',
  templateUrl: './filter-panel.component.html',
  styleUrls: ['./filter-panel.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatMenuModule,
    MatIcon,
    CheckboxFieldComponent,
    RmsDateComponent,
    InputFieldComponent,
    SelectFieldComponent,
    NumberFieldComponent
  ]
})
export class FilterPanelComponent implements OnInit, OnDestroy {
  @Input() columns: Column[] = [];
  @Input() tableId = '';

  @Output() filtersUpdated = new EventEmitter<FilterMap>();
  @Output() OnClose = new EventEmitter<void>();

  private readonly destroy$ = new Subject<void>();
  private readonly filtersSubject = new BehaviorSubject<FilterMap>({});

  readonly filters$ = this.filtersSubject.asObservable();

  SelectedFilter: FilterPreset | null = null;
  SystemDefaultFilters: FilterPreset[] = [];
  CustomFilters: FilterPreset[] = [];
  DefaultFilter: FilterPreset | null = null;
  loadingPresets = false;
  savingPreset = false;

  constructor(
    private filterService: FilterService,
    private messageService: RmsMessageService
  ) { }

  async ngOnInit(): Promise<void> {
    if (!this.tableId) {
      console.warn('FilterPanelComponent should receive a unique tableId.');
    }

    this.filterService.filters$
      .pipe(takeUntil(this.destroy$))
      .subscribe(payload => {
        if (!payload || payload.tableId !== this.tableId) return;
        this.setLocalFilters(payload.filters, false);
      });

    const existingFilters = this.filterService.getCurrentFilters(this.tableId);
    if (Object.keys(existingFilters).length) {
      this.setLocalFilters(existingFilters, false);
    }

    await this.loadPresetFilters(false);
    try {
      this.setLocalFilters(await this.filterService.initializeFilters(this.tableId), false);
      const current = JSON.stringify(this.filterService.getCurrentFilters(this.tableId));
      this.SelectedFilter = [...this.CustomFilters, ...this.SystemDefaultFilters].find(preset => {
        try { return JSON.stringify(this.filterService.parsePresetFilters(preset.FilterString ?? '{}')) === current; }
        catch { return false; }
      }) ?? null;
    } catch (error) {
      console.error('Unable to restore table filters.', error);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadPresetFilters(applyDefault: boolean): Promise<void> {
    this.loadingPresets = true;

    try {
      const presets = await this.filterService.getPresetFilters(this.tableId);
      this.setPresetList(Array.isArray(presets) ? presets : []);

      if (applyDefault && this.DefaultFilter) {
        this.selectPreset(this.DefaultFilter);
      }
    } catch (error) {
      console.error('Unable to load filter presets.', error);
      this.messageService.error('Unable to load preset filters.');
    } finally {
      this.loadingPresets = false;
    }
  }

  private setPresetList(presets: FilterPreset[]): void {
    const currentUser = this.filterService.getCurrentUserName().toLowerCase();

    const isDefaultForCurrentUser = (preset: FilterPreset): boolean => {
      if (preset.DefaultFilter) return true;
      if (!preset.DefaultedBy || !currentUser) return false;

      return preset.DefaultedBy
        .split(',')
        .map(value => value.trim().toLowerCase())
        .includes(currentUser);
    };

    this.SystemDefaultFilters = presets.filter(preset => !!preset.SystemCreated);
    this.CustomFilters = presets.filter(preset => !preset.SystemCreated &&
      preset.UserName?.toLowerCase() === currentUser);
    this.DefaultFilter = presets.find(isDefaultForCurrentUser) ?? null;
  }

  SelectPreset(preset: FilterPreset): void {
    this.selectPreset(preset);
  }

  async DeletePreset(preset: FilterPreset, event: Event): Promise<void> {
    event.stopPropagation();
    if (this.savingPreset || !this.CustomFilters.includes(preset) || preset.SystemCreated) return;
    if (!confirm(`Delete "${preset.FilterPresetName}" from My filters?`)) return;
    this.savingPreset = true;
    try {
      await this.filterService.deletePreset(preset);
      if (this.SelectedFilter?.FilterPresetID === preset.FilterPresetID) this.SelectedFilter = null;
      await this.loadPresetFilters(false);
      this.messageService.success('Saved filter deleted. Your current filtering is unchanged.');
    } catch (error) {
      this.messageService.error('Unable to delete this saved filter.');
    } finally {
      this.savingPreset = false;
    }
  }

  private selectPreset(preset: FilterPreset): void {
    if (!preset?.FilterString) return;

    try {
      const filters = this.filterService.parsePresetFilters(preset.FilterString);
      this.SelectedFilter = { ...preset };
      this.syncSelectedColumns(filters);
      this.setLocalFilters(filters, true);
    } catch (error) {
      console.error('Unable to parse selected filter preset.', error);
      this.messageService.error('This preset filter could not be loaded.');
    }
  }

  private syncSelectedColumns(filters: FilterMap): void {
    this.columns = (this.columns ?? []).map(column => {
      const key = this.getColumnKey(column);
      return {
        ...column,
        selected: !!key && Object.prototype.hasOwnProperty.call(filters, key)
      };
    });
  }

  updateFilters(column: Column, userInitiated = false): void {
    if (userInitiated && this.SelectedFilter?.SystemCreated) {
      this.SelectedFilter = null;
    }

    const key = this.getColumnKey(column);
    if (!key) return;

    const current = this.filtersSubject.getValue();
    const updated: FilterMap = { ...current };

    if ((column as any).selected) {
      updated[key] = current[key]
        ? this.cloneFilter(current[key])
        : this.createDefaultFilter(column);
    } else {
      delete updated[key];
    }

    this.setLocalFilters(updated, true);
  }

  toggleColumn(column: Column): void {
    (column as any).selected = !(column as any).selected;
    this.updateFilters(column, true);
  }

  clearFilters(): void {
    this.SelectedFilter = null;
    this.columns = (this.columns ?? []).map(column => ({
      ...column,
      selected: false
    }));
    this.setLocalFilters({}, true);
  }

  UpdateFilterType(column: Column, newFilterType: string): void {
    const key = this.getColumnKey(column);
    if (!key) return;

    const current = this.filtersSubject.getValue();
    const existing = current[key] ?? this.createDefaultFilter(column);
    const value = this.createValueForType(column, newFilterType, existing.value);

    const updated: FilterMap = {
      ...current,
      [key]: {
        FilterType: newFilterType,
        value
      }
    };

    (column as any).selected = true;
    this.SelectedFilter = this.SelectedFilter?.SystemCreated ? null : this.SelectedFilter;
    this.setLocalFilters(updated, true);
  }

  UpdateFilterValue(column: Column, newValue: any): void {
    const key = this.getColumnKey(column);
    if (!key) return;

    const current = this.filtersSubject.getValue();
    const existing = current[key] ?? this.createDefaultFilter(column);

    let value: any;
    if (existing.FilterType === 'Between' && newValue?.valueType) {
      value = {
        ...(existing.value ?? {}),
        [newValue.valueType]: newValue.value
      };
    } else if (existing.FilterType === 'Empty' || existing.FilterType === 'Not Empty') {
      value = null;
    } else {
      value = newValue;
    }

    const updated: FilterMap = {
      ...current,
      [key]: {
        ...existing,
        value
      }
    };

    (column as any).selected = true;
    this.setLocalFilters(updated, true);
  }

  getFilterValue(column: Column): Filter {
    const key = this.getColumnKey(column);
    return key && this.filtersSubject.getValue()[key]
      ? this.filtersSubject.getValue()[key]
      : this.createDefaultFilter(column);
  }

  getFilterType(column: Column): string {
    if (column.SearchType === 'select') return 'select';
    if (column.type === 'select') return 'text';
    return column.type || 'text';
  }

  getSelectOptions(column: Column): Array<{ label: string; value: any }> {
    if (column.SearchType === 'select') {
      return [
        { label: 'Equals', value: '=' },
        { label: 'Does not equal', value: '!=' },
        { label: 'Empty', value: 'Empty' },
        { label: 'Not empty', value: 'Not Empty' }
      ];
    }
    switch (column.type) {
      case 'checkbox':
        return [
          { label: 'Checked', value: '=' },
          { label: 'Unchecked', value: '!=' }
        ];
      case 'number':
        return [
          { label: 'Equals', value: '=' },
          { label: 'Greater than', value: '>' },
          { label: 'Less than', value: '<' },
          { label: 'Between', value: 'Between' },
          { label: 'Empty', value: 'Empty' },
          { label: 'Not empty', value: 'Not Empty' }
        ];
      case 'date':
        return [
          { label: 'On date', value: '=' },
          { label: 'After', value: '>' },
          { label: 'Before', value: '<' },
          { label: 'Between', value: 'Between' },
          { label: 'Empty', value: 'Empty' },
          { label: 'Not empty', value: 'Not Empty' }
        ];
      default:
        return [
          { label: 'Equals', value: '=' },
          { label: 'Does not equal', value: '!=' },
          { label: 'Contains', value: 'contains' },
          { label: 'Does not contain', value: 'notContains' },
          { label: 'Empty', value: 'Empty' },
          { label: 'Not empty', value: 'Not Empty' }
        ];
    }
  }

  async SaveFilter(): Promise<void> {
    const defaultName = this.SelectedFilter?.FilterPresetName ?? '';
    const filterPresetName = prompt('Enter a name for your custom filter:', defaultName)?.trim();

    if (!filterPresetName) {
      this.messageService.error('Filter name is required.');
      return;
    }

    this.savingPreset = true;

    try {
      await this.filterService.saveFilters({
        tableId: this.tableId,
        filters: this.filtersSubject.getValue(),
        FilterPresetName: filterPresetName,
        DefaultFilter: this.SelectedFilter?.DefaultFilter ?? false,
        SystemCreated: false,
        DefaultedBy: ''
      });

      await this.loadPresetFilters(false);
      this.SelectedFilter = this.CustomFilters.find(
        preset => preset.FilterPresetName === filterPresetName
      ) ?? null;
      this.messageService.success('Filter saved successfully.');
    } catch (error) {
      console.error('Unable to save filter preset.', error);
      this.messageService.error('Error saving filter.');
    } finally {
      this.savingPreset = false;
    }
  }

  async UpdateFilter(): Promise<void> {
    if (!this.SelectedFilter?.FilterPresetID || this.SelectedFilter.SystemCreated) return;

    this.savingPreset = true;

    try {
      await this.filterService.saveFilters({
        tableId: this.tableId,
        filters: this.filtersSubject.getValue(),
        FilterPresetID: this.SelectedFilter.FilterPresetID,
        FilterPresetName: this.SelectedFilter.FilterPresetName,
        DefaultFilter: this.SelectedFilter.DefaultFilter ?? false,
        SystemCreated: false,
        DefaultedBy: this.SelectedFilter.DefaultedBy ?? ''
      });

      await this.loadPresetFilters(false);
      this.messageService.success('Filter updated successfully.');
    } catch (error) {
      console.error('Unable to update filter preset.', error);
      this.messageService.error('Error updating filter.');
    } finally {
      this.savingPreset = false;
    }
  }

  async UpdateDefault(): Promise<void> {
    if (!this.SelectedFilter || this.SelectedFilter.SystemCreated) return;
    await this.UpdateFilter();
  }

  closePanel(): void {
    this.OnClose.emit();
  }

  trackColumn(_: number, column: Column): string {
    return this.getColumnKey(column);
  }

  private setLocalFilters(filters: FilterMap, publish: boolean): void {
    const cloned = this.cloneFilters(filters);
    this.syncSelectedColumns(cloned);
    this.filtersSubject.next(cloned);

    if (publish) {
      this.filterService.updateFilters(this.tableId, cloned);
      this.filtersUpdated.emit(cloned);
    }
  }

  private getColumnKey(column: Column): string {
    return column.key || column.labelKey || '';
  }

  private cloneFilter(filter: Filter): Filter {
    return {
      ...filter,
      value: filter?.value && typeof filter.value === 'object'
        ? { ...filter.value }
        : filter?.value
    };
  }

  private cloneFilters(filters: FilterMap): FilterMap {
    return Object.fromEntries(
      Object.entries(filters ?? {}).map(([key, filter]) => [key, this.cloneFilter(filter)])
    );
  }

  private createDefaultFilter(column: Column): Filter {
    if (column.SearchType === 'select') return { FilterType: '=', value: null };
    switch (column.type) {
      case 'number':
        return { FilterType: '=', value: null };
      case 'date':
        return { FilterType: 'Between', value: { StartDate: null, EndDate: null } };
      case 'checkbox':
        return { FilterType: '=', value: true };
      default:
        return { FilterType: 'contains', value: '' };
    }
  }

  private createValueForType(column: Column, filterType: string, currentValue: any): any {
    if (filterType === 'Empty' || filterType === 'Not Empty') return null;
    if (filterType === 'Between') {
      return column.type === 'date'
        ? { StartDate: null, EndDate: null }
        : { Start: null, End: null };
    }
    if (column.type === 'checkbox') return filterType === '=';
    if (currentValue && typeof currentValue !== 'object') return currentValue;
    return null;
  }
}
